import { selectCheckout, selectOrder, useAppSelector } from '@open-tender/cloud'
import { formatCartCap } from 'utils/closingCartCap'

interface RequestedAtError {
  code?: string
  message?: string
  max_cart_total?: string
}

// @open-tender/types doesn't know about max_cart_total or
// requested_at_original, so read them through this local shape.
interface CheckWithCap {
  errors?: Record<string, RequestedAtError>
  requested_at?: string
  requested_at_original?: string | null
  revenue_center?: { revenue_center_id?: number }
}

export interface ClosingCartCap {
  cap: number
  capLabel: string
  message: string
}

/**
 * Cart-total ceiling from the last server check: the backend's
 * requested_at.closing_time_exceeded error carries max_cart_total (the largest
 * total that is still ready before close) when shrinking the cart would help.
 * No network call -- callers compare it against the live cart total, so the
 * cart sidebar can react to every quantity change.
 */
export const useClosingCartCap = (): ClosingCartCap | null => {
  const { check } = useAppSelector(selectCheckout)
  const { requestedAt, revenueCenter } = useAppSelector(selectOrder)
  const c = check as unknown as CheckWithCap | null
  const error = c?.errors?.['$.requested_at']
  if (!error?.max_cart_total) return null

  // Only trust the cap for the order it was computed for -- a different
  // pickup time or location since that check makes it meaningless.
  const checkedRequestedAt = c?.requested_at_original ?? c?.requested_at
  const checkedRcId = c?.revenue_center?.revenue_center_id
  if (checkedRequestedAt !== requestedAt) return null
  if (checkedRcId !== revenueCenter?.revenue_center_id) return null

  const cap = Number(error.max_cart_total)
  if (!Number.isFinite(cap) || cap <= 0) return null
  return {
    cap,
    capLabel: formatCartCap(cap),
    message: error.message ?? '',
  }
}
